/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import {
  NavHeaderAccountUrls,
  NavHeaderMessages,
  NavHeaderUser,
  TesterNavigationHeaderService,
} from '@ng-falcon/features/tester-navigation-header/tester-navigation-header.service';

@Injectable({
  providedIn: 'root',
})
export class UtestNavigationHeaderService implements TesterNavigationHeaderService {
  readonly avatarChanged$: Observable<void> = EMPTY;

  constructor(
    // Standalone components downgraded via downgradeComponent don't share the
    // NgModule injector bridge to AngularJS, so AngularJS services cannot be
    // injected directly by name. $injector IS provided at the Angular root level
    // by UpgradeModule, and we resolve AngularJS services lazily from it instead.
    @Inject('$injector') private $injector: any,
  ) {}

  private get userService(): any { return this.$injector.get('UserService'); }
  private get configService(): any { return this.$injector.get('ConfigService'); }
  private get featureFlags(): any { return this.$injector.get('featureFlags'); }

  async getCurrentUser(): Promise<NavHeaderUser> {
    const res = await this.userService.me();
    return {
      firstName: res.data.first_name ?? '',
      lastName: res.data.last_name ?? '',
      platformId: res.data.platform_id,
      isTester: res.data.is_tester === true,
      isTTL: res.data.platform_roles?.ttl === true,
      isLite: res.data.registration_type === 'LITE',
    };
  }

  async getUnreadMessages(): Promise<NavHeaderMessages> {
    const res = await this.userService.unreadMessages();
    return {
      hasUnread: res.data.has_unread_messages ?? false,
      count: res.data.unseen_mention_count ?? 0,
    };
  }

  async getUnreadActivities(): Promise<NavHeaderMessages> {
    const res = await this.userService.me();
    const count = res?.data?.unread_activities_count ?? 0;
    return {
      hasUnread: count > 0,
      count,
    };
  }

  async getChatUrl(): Promise<string | null> {
    const res = await this.configService.config();
    return res.data.chat_url ?? null;
  }

  async getAccountUrls(): Promise<NavHeaderAccountUrls> {
    const res = await this.configService.config();
    const base = res.data.profile_url;
    return {
      accountSettings: `${base}/account/settings`,
      accountSecurity: `${base}/account/security`,
      accountAdvanced: `${base}/account/advanced`,
    };
  }

  hasFeature(flag: string): boolean {
    return this.featureFlags.isOn(flag);
  }

  logout(): void {
    window.location.href = '/logout';
  }
}
